import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Info, FileDown, FileSpreadsheet } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { EdrUnmatchedRows } from './EdrUnmatchedRows';

// ===== Money formatting =====
const fmt = (val, currency = 'USD') => {
  if (val == null || isNaN(val)) return currency === 'USD' ? 'U$D 0' : '$0';
  const symbol = currency === 'USD' ? 'U$D ' : '$';
  const abs = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${symbol}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${symbol}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${symbol}${abs.toFixed(0)}`;
};

// Exact formatter (sin abreviación K/M) para el modal de detalle.
const fmtExact = (val, currency = 'USD') => {
  if (val == null || isNaN(val)) return currency === 'USD' ? 'U$D 0' : '$0';
  const symbol = currency === 'USD' ? 'U$D ' : '$';
  const sign = val < 0 ? '-' : '';
  const abs = Math.abs(val);
  return `${sign}${symbol}${abs.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};

const monthLabel = (key) => {
  const parts = key.split('-');
  const names = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${names[parseInt(parts[1]) - 1]} ${parts[0].slice(2)}`;
};

const flattenCampaigns = (yearsMap, unit) => {
  const all = [];
  Object.values(yearsMap || {}).forEach((y) => {
    const src = y?.[unit];
    if (src?.campaigns) all.push(...src.campaigns);
  });
  return all;
};

const PnLRow = ({ label, value, currency = 'USD', isSubtotal = false, sublabel = null, pending = false, accent = '#FFD166', dim = false, onDetail = null }) => {
  const cls = isSubtotal ? 'border-t border-gray-700 pt-2 mt-1' : '';
  const labelCls = isSubtotal
    ? 'text-sm font-bold uppercase tracking-wider text-gray-200'
    : dim
      ? 'text-sm text-gray-400 pl-4'
      : 'text-sm text-gray-300';
  const valCls = isSubtotal ? 'text-lg font-bold' : 'text-base font-semibold';
  const color = pending ? '#6b7280' : value < 0 ? '#E63946' : isSubtotal ? accent : '#e5e7eb';
  return (
    <div className={`flex items-baseline justify-between gap-4 py-2 ${cls}`}>
      <div className="flex flex-col flex-1 min-w-0">
        <span className="flex items-center gap-2">
          <span className={labelCls} style={{ fontFamily: isSubtotal ? "'Bebas Neue', sans-serif" : "'DM Sans', sans-serif", letterSpacing: isSubtotal ? '0.04em' : '0' }}>
            {label}
          </span>
          {onDetail && (
            <button
              type="button"
              onClick={onDetail}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-500 hover:text-yellow-400 hover:bg-gray-800 transition-colors"
              title="Ver detalle"
              data-testid={`edr-detail-btn-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
            >
              <Info size={13} />
            </button>
          )}
        </span>
        {sublabel && <span className="text-[10px] text-gray-500 mt-0.5">{sublabel}</span>}
      </div>
      <span className={valCls} style={{ color, fontFamily: "'DM Sans', sans-serif" }}>
        {pending ? <span className="text-xs italic text-gray-500">pendiente</span> : fmt(value, currency)}
      </span>
    </div>
  );
};

const Pct = ({ value, accent = '#FFD166' }) => (
  <span className="text-xs font-mono ml-2" style={{ color: accent }}>
    ({(value || 0).toFixed(1)}%)
  </span>
);

// Helpers de export (PDF/Excel) para el modal de detalle.
const _slug = (s) => (s || 'detalle').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const exportDetailExcel = ({ title, subtitle, rows, columns, totalUsd }) => {
  if (!rows || rows.length === 0) return;
  // Encabezado tabular + totales.
  const headerRow = columns.map((c) => c.label);
  const dataRows = rows.map((r) => columns.map((c) => {
    const raw = c.render ? c.render(r) : r[c.key];
    // XLSX acepta strings; para montos exportamos el valor numérico crudo cuando podemos.
    const key = c.key;
    if (['valor_ars', 'costo_ars', 'sueldos_ars', 'bonos_ars', 'cargas_ars', 'ars'].includes(key)) return Number(r[key] || 0);
    if (['valor_usd', 'costo_usd', 'sueldos_usd', 'bonos_usd', 'cargas_usd', 'usd'].includes(key)) return Number(r[key] || 0);
    if (key === 'rate') return Number(r.rate || 0);
    return raw;
  }));
  const totalRow = columns.map((c, i) => (i === columns.length - 1 ? totalUsd : (i === columns.length - 2 ? 'TOTAL USD' : '')));
  const aoa = [[title], subtitle ? [subtitle] : [''], [], headerRow, ...dataRows, [], totalRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
  XLSX.writeFile(wb, `edr-${_slug(title)}.xlsx`);
};

const exportDetailPdf = ({ title, subtitle, rows, columns, totalUsd }) => {
  if (!rows || rows.length === 0) return;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(title, 40, 40);
  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(subtitle, 40, 56, { maxWidth: 760 });
    doc.setTextColor(0);
  }
  autoTable(doc, {
    startY: subtitle ? 76 : 60,
    head: [columns.map((c) => c.label)],
    body: rows.map((r) => columns.map((c) => {
      const v = c.render ? c.render(r) : r[c.key];
      return v == null ? '—' : String(v);
    })),
    foot: [[
      ...Array(columns.length - 1).fill(''),
      `TOTAL: U$D ${Number(totalUsd || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`,
    ]],
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [255, 209, 102], textColor: 0, fontStyle: 'bold' },
    footStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold', halign: 'right' },
    columnStyles: columns.reduce((acc, c, i) => {
      if (c.align === 'right') acc[i] = { halign: 'right' };
      return acc;
    }, {}),
  });
  doc.save(`edr-${_slug(title)}.pdf`);
};

// Modal de detalle: muestra lista de facturas o descripción textual del ítem.
const DetailModal = ({ open, onOpenChange, title, subtitle, rows, columns, totalUsd, note }) => {
  const [query, setQuery] = useState('');
  React.useEffect(() => { if (!open) setQuery(''); }, [open]);
  const filteredRows = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      Object.values(r || {}).some((v) => String(v ?? '').toLowerCase().includes(q))
    );
  }, [rows, query]);
  const filteredTotalUsd = useMemo(() => {
    if (!filteredRows || !filteredRows.length) return 0;
    // Suma USD de las filas visibles — usa valor_usd o costo_usd o usd según columna.
    const keys = ['valor_usd', 'costo_usd', 'sueldos_usd', 'bonos_usd', 'cargas_usd', 'usd'];
    return filteredRows.reduce((acc, r) => {
      for (const k of keys) if (r[k] != null) return acc + Number(r[k] || 0);
      return acc;
    }, 0);
  }, [filteredRows]);
  const showFilteredTotal = query.trim() && filteredRows.length !== (rows || []).length;
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="bg-gray-950 border-gray-800 text-white max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
      data-testid="edr-detail-modal"
    >
      <DialogHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-xl text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              {title}
            </DialogTitle>
            {subtitle && <DialogDescription className="text-xs text-gray-400">{subtitle}</DialogDescription>}
          </div>
          {rows && rows.length > 0 && (
            <div className="flex items-center gap-2 shrink-0 mr-6">
              <button
                type="button"
                onClick={() => exportDetailExcel({ title, subtitle, rows: filteredRows, columns, totalUsd: showFilteredTotal ? filteredTotalUsd : totalUsd })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-semibold transition-colors"
                data-testid="edr-detail-export-excel"
                title="Exportar a Excel"
              >
                <FileSpreadsheet size={14} />
                Excel
              </button>
              <button
                type="button"
                onClick={() => exportDetailPdf({ title, subtitle, rows: filteredRows, columns, totalUsd: showFilteredTotal ? filteredTotalUsd : totalUsd })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs font-semibold transition-colors"
                data-testid="edr-detail-export-pdf"
                title="Exportar a PDF"
              >
                <FileDown size={14} />
                PDF
              </button>
            </div>
          )}
        </div>
        {rows && rows.length > 0 && (
          <div className="mt-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar (ej: 1474, Vrancic Emi, Sernova)…"
              className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
              data-testid="edr-detail-search"
            />
          </div>
        )}
      </DialogHeader>
      <div className="flex-1 overflow-auto">
        {filteredRows && filteredRows.length > 0 ? (
          <table className="w-full text-xs" data-testid="edr-detail-table">
            <thead className="sticky top-0 bg-gray-950 z-10 border-b border-gray-800">
              <tr className="text-left text-gray-400 uppercase tracking-wider">
                {columns.map((c) => (
                  <th key={c.key} className={`px-3 py-2 font-semibold ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => (
                <tr key={i} className="border-b border-gray-900 hover:bg-gray-900/50">
                  {columns.map((c) => (
                    <td key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right font-mono' : 'text-gray-200'}`}>
                      {c.render ? c.render(r) : (r[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-gray-950 border-t-2 border-yellow-500/40">
              <tr>
                <td colSpan={columns.length - 1} className="px-3 py-3 text-right text-sm font-bold uppercase tracking-wider text-gray-300">
                  {showFilteredTotal
                    ? `Total filtrado (${filteredRows.length} de ${rows.length})`
                    : `Total (${filteredRows.length} fila${filteredRows.length === 1 ? '' : 's'})`}
                </td>
                <td className="px-3 py-3 text-right text-base font-bold text-yellow-400 font-mono">
                  {fmtExact(showFilteredTotal ? filteredTotalUsd : totalUsd, 'USD')}
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="p-8 text-center text-sm text-gray-500">
            {query.trim() ? `Sin resultados para "${query}".` : (note || 'Sin filas para este período.')}
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
  );
};

// ===== EDR view =====
// New layout (2026-08-13): AR and INTL each show their own labor breakdown
// (sueldos + bonos + cargas sociales) side by side, then unified common expenses.
// NOTE 2026-02: La fila Gaston/Rentabilidad fue eliminada por pedido del usuario,
// así que el Resultado Final = Resultado Operativo.
const EdrView = ({ yearsMap, rateFor, edr }) => {
  const YEAR = '2026';
  const monthsAll2026 = useMemo(() => Array.from({ length: 12 }, (_, i) => `${YEAR}-${String(i + 1).padStart(2, '0')}`), []);
  const currentMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [selection, setSelection] = useState(YEAR);
  const [detailKey, setDetailKey] = useState(null);  // 'ventaAr' | 'costoAr' | 'ventaIntl' | 'costoIntl' | 'sueldosAr' | 'bonosAr' | 'cargasAr' | 'sueldosIntl' | 'bonosIntl' | 'cargasIntl' | 'gastosComunes'

  const isYear = selection === YEAR;

  const data = useMemo(() => {
    const monthsInScope = isYear
      ? monthsAll2026.filter((m) => m !== currentMonthKey)
      : [selection];
    const arAll = flattenCampaigns(yearsMap, 'ar');
    const intlAll = flattenCampaigns(yearsMap, 'intl');

    const fxOf = (m) => {
      const e = edr?.fx_rates?.[m];
      return e && e > 0 ? e : rateFor(m);
    };
    const fx = isYear ? 0 : fxOf(selection);
    const edrFx = isYear ? null : edr?.fx_rates?.[selection];

    // Opex (sueldos/bonos/cargas/common) acumulan en el loop por mes.
    // Ventas/Costos se derivan directamente de invoicesAr/Intl al final
    // (fuente única de verdad, consistente con el modal de detalle).
    const ar = { sueldos: 0, bonos: 0, cargas: 0 };
    const intl = { sueldos: 0, bonos: 0, cargas: 0, gastos_financieros: 0, otros_gastos: 0 };
    let commonUsd = 0;
    let hasAnyOpex = false;

    // Breakdowns para el modal de detalle
    const invoicesAr = [];     // facturas AR del período
    const invoicesIntl = [];   // facturas INTL del período
    const opexArBreakdown = [];
    const opexIntlBreakdown = [];
    const commonBreakdown = [];

    for (const m of monthsInScope) {
      const rate = fxOf(m);
      const arsToUsd = (ars) => (rate > 0 ? ars / rate : 0);

      for (const c of arAll) {
        if (c.month !== m) continue;
        const valorUsd = arsToUsd(c.valor || 0);
        const costoUsd = arsToUsd(c.costo_ars || 0);
        invoicesAr.push({
          month: m,
          nro: c.nro_factura || '—',
          fecha: c.fecha || '—',
          kam: c.kam || '—',
          influencer: c.influencer || '—',
          marca: c.marca || c.real_agencia || '—',
          valor_ars: c.valor || 0,
          costo_ars: c.costo_ars || 0,
          valor_usd: valorUsd,
          costo_usd: costoUsd,
          rate,
          is_canje: !!c.is_canje,
        });
      }
      for (const c of intlAll) {
        if (c.month !== m) continue;
        const vUsd = (c.valor || 0) + arsToUsd(c.valor_ars_extra || 0);
        const cUsd = (c.costo_usd || 0) + arsToUsd(c.costo_ars || 0);
        invoicesIntl.push({
          month: m,
          nro: c.nro_factura || '—',
          fecha: c.fecha || '—',
          kam: c.kam || '—',
          influencer: c.influencer || '—',
          marca: c.marca || c.real_agencia || '—',
          valor_usd: vUsd,
          costo_usd: cUsd,
          rate,
          is_canje: !!c.is_canje,
        });
      }

      const arM = edr?.ar_opex?.[m];
      if (arM) {
        hasAnyOpex = true;
        const sUsd = arsToUsd(arM.sueldos || 0);
        const bUsd = arsToUsd(arM.bonos || 0);
        const cUsd = arsToUsd(arM.cargas_sociales || 0);
        ar.sueldos += sUsd;
        ar.bonos   += bUsd;
        ar.cargas  += cUsd;
        opexArBreakdown.push({ month: m, sueldos_ars: arM.sueldos || 0, bonos_ars: arM.bonos || 0, cargas_ars: arM.cargas_sociales || 0,
                               sueldos_usd: sUsd, bonos_usd: bUsd, cargas_usd: cUsd, rate });
      }
      const intlM = edr?.intl_opex?.[m];
      if (intlM) {
        hasAnyOpex = true;
        // Sueldos y Cargas Sociales INTL vienen YA en USD desde la pestaña EDR;
        // no se re-convierte. Lo mismo para "Gastos Financieros" (Gasto Liquidez
        // + Costo Financiero), que se detectan en el tope del Excel.
        const sUsd = (intlM.sueldos || 0);
        const bUsd = (intlM.bonos || 0);
        const cUsd = (intlM.cargas_sociales || 0);
        const fUsd = (intlM.gastos_financieros || 0);
        const oUsd = (intlM.otros_gastos || 0);
        intl.sueldos += sUsd;
        intl.bonos   += bUsd;
        intl.cargas  += cUsd;
        intl.gastos_financieros += fUsd;
        intl.otros_gastos += oUsd;
        opexIntlBreakdown.push({ month: m, sueldos_usd_raw: intlM.sueldos || 0, bonos_usd_raw: intlM.bonos || 0,
                                 cargas_usd_raw: intlM.cargas_sociales || 0, fin_usd_raw: intlM.gastos_financieros || 0,
                                 otros_usd_raw: intlM.otros_gastos || 0,
                                 sueldos_usd: sUsd, bonos_usd: bUsd, cargas_usd: cUsd, fin_usd: fUsd, otros_usd: oUsd, rate });
      }
      const cm = edr?.common_opex?.[m];
      const cmBreakdown = edr?.common_breakdown?.[m];
      if (cm != null && cm > 0) {
        hasAnyOpex = true;
        const cUsd = arsToUsd(cm);
        commonUsd += cUsd;
        commonBreakdown.push({
          month: m,
          ars: cm,
          usd: cUsd,
          rate,
          items: (cmBreakdown && cmBreakdown.common) || null,
        });
      }
    }

    const arOpexTotal = ar.sueldos + ar.bonos + ar.cargas;
    const intlOpexTotal = intl.sueldos + intl.bonos + intl.cargas + intl.gastos_financieros + intl.otros_gastos;

    // ✅ Fuente única de verdad: Ventas / Costos se derivan del MISMO array
    // que alimenta el modal de detalle (invoicesAr/Intl). Esto garantiza
    // que lo que ves sumando en el detalle == el total mostrado en los
    // bloques AR/INTL. Antes se calculaban en loops paralelos y podían
    // divergir (bug reportado con factura 1474 — Sep 26).
    // Aplicamos el MISMO filtro que el modal: excluir canje para Ventas
    // (canje no es venta real); costos solo cuentan cuando costo_usd > 0.
    const ventaArUsd = invoicesAr
      .filter((r) => !r.is_canje)
      .reduce((acc, r) => acc + (r.valor_usd || 0), 0);
    const costoArUsd = invoicesAr
      .reduce((acc, r) => acc + (r.costo_usd || 0), 0);
    const ventaIntlUsd = invoicesIntl
      .filter((r) => !r.is_canje)
      .reduce((acc, r) => acc + (r.valor_usd || 0), 0);
    const costoIntlUsd = invoicesIntl
      .reduce((acc, r) => acc + (r.costo_usd || 0), 0);

    const resultadoAr = ventaArUsd - costoArUsd - arOpexTotal;
    const resultadoIntl = ventaIntlUsd - costoIntlUsd - intlOpexTotal;
    const resultadoCombinado = resultadoAr + resultadoIntl;
    const resultadoOperativo = resultadoCombinado - commonUsd;
    // Resultado Final = Resultado Operativo (Gaston/Rentabilidad eliminado).
    const resultadoFinal = resultadoOperativo;

    const ventaTotal = ventaArUsd + ventaIntlUsd;
    const margenArPct = ventaArUsd > 0 ? (resultadoAr / ventaArUsd) * 100 : 0;
    const margenIntlPct = ventaIntlUsd > 0 ? (resultadoIntl / ventaIntlUsd) * 100 : 0;
    const margenFinalPct = ventaTotal > 0 ? (resultadoFinal / ventaTotal) * 100 : 0;

    return {
      fx, edrFx,
      ventaArUsd, costoArUsd, ar, arOpexTotal, resultadoAr, margenArPct,
      ventaIntlUsd, costoIntlUsd, intl, intlOpexTotal, resultadoIntl, margenIntlPct,
      resultadoCombinado,
      commonUsd, resultadoOperativo,
      resultadoFinal, margenFinalPct,
      ventaTotal, hasAnyOpex,
      // breakdowns
      invoicesAr, invoicesIntl, opexArBreakdown, opexIntlBreakdown, commonBreakdown,
    };
  }, [yearsMap, rateFor, edr, isYear, selection, monthsAll2026, currentMonthKey]);

  const periodLabel = isYear
    ? `Total ${YEAR} (excluye ${monthLabel(currentMonthKey)} — mes en curso)`
    : `${monthLabel(selection)} · TC ${data.fx.toLocaleString('es-AR', { maximumFractionDigits: 0 })}${data.edrFx ? ' (EDR)' : ' (FX panel)'}`;

  // ======== Configuración del modal de detalle según `detailKey` ========
  const detailConfig = useMemo(() => {
    if (!detailKey) return null;
    const periodStr = isYear ? `Total ${YEAR}` : monthLabel(selection);
    switch (detailKey) {
      case 'ventaAr':
        return {
          title: `Ventas AR — ${periodStr}`,
          subtitle: `Facturas de clientes argentinos del período (convertidas a USD con el TC mensual).`,
          rows: data.invoicesAr.filter(r => !r.is_canje),
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'nro', label: 'Nº Factura' },
            { key: 'kam', label: 'Comercial' },
            { key: 'influencer', label: 'Influencer' },
            { key: 'marca', label: 'Marca / Agencia' },
            { key: 'valor_ars', label: 'Valor ARS', align: 'right', render: (r) => r.valor_ars.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'rate', label: 'TC', align: 'right', render: (r) => r.rate.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'valor_usd', label: 'Valor USD', align: 'right', render: (r) => fmtExact(r.valor_usd, 'USD') },
          ],
          totalUsd: data.ventaArUsd,
        };
      case 'costoAr':
        return {
          title: `Costos Directos AR — ${periodStr}`,
          subtitle: `Comisión Talento por factura (col Z del Excel), convertida a USD con el TC mensual.`,
          rows: data.invoicesAr.filter(r => r.costo_ars > 0),
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'nro', label: 'Nº Factura' },
            { key: 'kam', label: 'Comercial' },
            { key: 'influencer', label: 'Influencer' },
            { key: 'costo_ars', label: 'Costo ARS', align: 'right', render: (r) => r.costo_ars.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'rate', label: 'TC', align: 'right', render: (r) => r.rate.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'costo_usd', label: 'Costo USD', align: 'right', render: (r) => fmtExact(r.costo_usd, 'USD') },
          ],
          totalUsd: data.costoArUsd,
        };
      case 'ventaIntl':
        return {
          title: `Ventas INTL — ${periodStr}`,
          subtitle: `Facturas de clientes internacionales del período.`,
          rows: data.invoicesIntl.filter(r => !r.is_canje),
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'nro', label: 'Nº Factura' },
            { key: 'kam', label: 'Comercial' },
            { key: 'influencer', label: 'Influencer' },
            { key: 'marca', label: 'Marca / Agencia' },
            { key: 'valor_usd', label: 'Valor USD', align: 'right', render: (r) => fmtExact(r.valor_usd, 'USD') },
          ],
          totalUsd: data.ventaIntlUsd,
        };
      case 'costoIntl':
        return {
          title: `Costos Directos INTL — ${periodStr}`,
          subtitle: `Costo talento por factura INTL.`,
          rows: data.invoicesIntl.filter(r => r.costo_usd > 0),
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'nro', label: 'Nº Factura' },
            { key: 'kam', label: 'Comercial' },
            { key: 'influencer', label: 'Influencer' },
            { key: 'costo_usd', label: 'Costo USD', align: 'right', render: (r) => fmtExact(r.costo_usd, 'USD') },
          ],
          totalUsd: data.costoIntlUsd,
        };
      case 'sueldosAr': {
        const CARGAS_LABELS = new Set(['CARGAS SOCIALES', 'MONOTRIBUTO', 'AUTONOMOS']);
        const flat = [];
        for (const m of (isYear ? monthsAll2026.filter(x => x !== currentMonthKey) : [selection])) {
          const detail = edr?.common_breakdown?.[m]?.ar_sueldos_detail || {};
          const rate = edr?.fx_rates?.[m] && edr.fx_rates[m] > 0 ? edr.fx_rates[m] : rateFor(m);
          for (const [person, ars] of Object.entries(detail)) {
            if (CARGAS_LABELS.has(person)) continue;
            flat.push({ month: m, persona: person, ars, rate, usd: rate > 0 ? ars / rate : 0 });
          }
        }
        return {
          title: `Sueldos Arg — ${periodStr}`,
          subtitle: `Lista persona-por-persona (sub-sección "DETALLE SUELDOS" bajo "Gastos Operativos" en EDR). ARS → USD con TC.`,
          rows: flat,
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'persona', label: 'Persona' },
            { key: 'ars', label: 'ARS', align: 'right', render: (r) => r.ars.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'rate', label: 'TC', align: 'right', render: (r) => r.rate.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'usd', label: 'USD', align: 'right', render: (r) => fmtExact(r.usd, 'USD') },
          ],
          totalUsd: data.ar.sueldos,
        };
      }
      case 'sueldosIntl': {
        const flat = [];
        for (const m of (isYear ? monthsAll2026.filter(x => x !== currentMonthKey) : [selection])) {
          const detail = edr?.common_breakdown?.[m]?.intl_sueldos_detail || {};
          for (const [person, usd] of Object.entries(detail)) {
            flat.push({ month: m, persona: person, usd });
          }
        }
        return {
          title: `Sueldos Inter — ${periodStr}`,
          subtitle: `Lista persona-por-persona (sub-sección "DETALLE SUELDOS" bajo "Gastos Operativos Int"). Ya en USD.`,
          rows: flat,
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'persona', label: 'Persona' },
            { key: 'usd', label: 'USD', align: 'right', render: (r) => fmtExact(r.usd, 'USD') },
          ],
          totalUsd: data.intl.sueldos,
        };
      }
      case 'bonosIntl': {
        const flat = [];
        for (const m of (isYear ? monthsAll2026.filter(x => x !== currentMonthKey) : [selection])) {
          const usd = edr?.common_breakdown?.[m]?.intl?.['BONOS COMERCIALES'] || 0;
          if (usd !== 0) flat.push({ month: m, concepto: 'Bonos Comerciales', usd });
        }
        return {
          title: `Bonos Inter — ${periodStr}`,
          subtitle: `Fila "Bonos Comerciales" bajo "Gastos Operativos Int" (uso excepcional, USD sin conversión).`,
          rows: flat,
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'concepto', label: 'Concepto' },
            { key: 'usd', label: 'USD', align: 'right', render: (r) => fmtExact(r.usd, 'USD') },
          ],
          totalUsd: data.intl.bonos,
        };
      }
      case 'bonosAr': {
        const flat = [];
        for (const m of (isYear ? monthsAll2026.filter(x => x !== currentMonthKey) : [selection])) {
          const ars = edr?.common_breakdown?.[m]?.ar?.['BONOS COMERCIALES'] || 0;
          const rate = edr?.fx_rates?.[m] && edr.fx_rates[m] > 0 ? edr.fx_rates[m] : rateFor(m);
          if (ars !== 0) flat.push({ month: m, concepto: 'Bonos Comerciales', ars, rate, usd: rate > 0 ? ars / rate : 0 });
        }
        return {
          title: `Bonos Arg — ${periodStr}`,
          subtitle: `Fila "Bonos Comerciales" bajo "Gastos Operativos" de la pestaña EDR.`,
          rows: flat,
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'concepto', label: 'Concepto' },
            { key: 'ars', label: 'ARS', align: 'right', render: (r) => r.ars.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'rate', label: 'TC', align: 'right', render: (r) => r.rate.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'usd', label: 'USD', align: 'right', render: (r) => fmtExact(r.usd, 'USD') },
          ],
          totalUsd: data.ar.bonos,
        };
      }
      case 'cargasAr': {
        const CARGAS_LABELS = ['CARGAS SOCIALES', 'MONOTRIBUTO', 'AUTONOMOS'];
        const flat = [];
        for (const m of (isYear ? monthsAll2026.filter(x => x !== currentMonthKey) : [selection])) {
          const arGroup = edr?.common_breakdown?.[m]?.ar || {};
          const rate = edr?.fx_rates?.[m] && edr.fx_rates[m] > 0 ? edr.fx_rates[m] : rateFor(m);
          for (const lbl of CARGAS_LABELS) {
            const ars = arGroup[lbl] || 0;
            if (ars !== 0) flat.push({ month: m, concepto: lbl, ars, rate, usd: rate > 0 ? ars / rate : 0 });
          }
        }
        return {
          title: `Cargas Sociales AR — ${periodStr}`,
          subtitle: `"Cargas Sociales" + "Monotributo" + "Autónomos" bajo "Gastos Operativos" de EDR.`,
          rows: flat,
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'concepto', label: 'Concepto' },
            { key: 'ars', label: 'ARS', align: 'right', render: (r) => r.ars.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'rate', label: 'TC', align: 'right', render: (r) => r.rate.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'usd', label: 'USD', align: 'right', render: (r) => fmtExact(r.usd, 'USD') },
          ],
          totalUsd: data.ar.cargas,
        };
      }
      case 'cargasIntl': {
        const CARGAS_LABELS = ['CARGAS SOCIALES', 'MONOTRIBUTO', 'AUTONOMOS'];
        const flat = [];
        for (const m of (isYear ? monthsAll2026.filter(x => x !== currentMonthKey) : [selection])) {
          const intlGroup = edr?.common_breakdown?.[m]?.intl || {};
          for (const lbl of CARGAS_LABELS) {
            const usd = intlGroup[lbl] || 0;
            if (usd !== 0) flat.push({ month: m, concepto: lbl, usd });
          }
        }
        return {
          title: `Cargas Sociales INT — ${periodStr}`,
          subtitle: `Cargas Sociales / Monotributo / Autónomos bajo "Gastos Operativos Int". Ya en USD.`,
          rows: flat,
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'concepto', label: 'Concepto' },
            { key: 'usd', label: 'USD', align: 'right', render: (r) => fmtExact(r.usd, 'USD') },
          ],
          totalUsd: data.intl.cargas,
        };
      }
      case 'finIntl': {
        const FIN_LABELS = ['GASTO LIQUIDEZ', 'COSTO FINANCIERO'];
        const flat = [];
        for (const m of (isYear ? monthsAll2026.filter(x => x !== currentMonthKey) : [selection])) {
          const intlGroup = edr?.common_breakdown?.[m]?.intl || {};
          for (const lbl of FIN_LABELS) {
            const usd = intlGroup[lbl] || 0;
            if (usd !== 0) flat.push({ month: m, concepto: lbl, usd });
          }
        }
        return {
          title: `Gastos Financieros INT — ${periodStr}`,
          subtitle: `"Gasto Liquidez" + "Costo Financiero" (tope de la pestaña EDR). Ya en USD.`,
          rows: flat,
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'concepto', label: 'Concepto' },
            { key: 'usd', label: 'USD', align: 'right', render: (r) => fmtExact(r.usd, 'USD') },
          ],
          totalUsd: data.intl.gastos_financieros,
        };
      }
      case 'otrosGastosIntl': {
        const OTROS_LABELS = ['DOMINIO G SUITES', 'COMPUTADORAS Y EQUIPOS', 'GASTOS EXTRAORDINARIOS'];
        const flat = [];
        for (const m of (isYear ? monthsAll2026.filter(x => x !== currentMonthKey) : [selection])) {
          const intlGroup = edr?.common_breakdown?.[m]?.intl || {};
          for (const lbl of OTROS_LABELS) {
            const usd = intlGroup[lbl] || 0;
            if (usd !== 0) flat.push({ month: m, concepto: lbl, usd });
          }
        }
        return {
          title: `Otros Gastos INT — ${periodStr}`,
          subtitle: `Dominio G Suites, Computadoras y Equipos, Gastos Extraordinarios (bajo "Gastos Operativos Int", USD).`,
          rows: flat,
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'concepto', label: 'Concepto' },
            { key: 'usd', label: 'USD', align: 'right', render: (r) => fmtExact(r.usd, 'USD') },
          ],
          totalUsd: data.intl.otros_gastos,
        };
      }
      case 'gastosComunes': {
        // Expandir cada mes en sus ítems de breakdown.
        const flat = [];
        for (const b of data.commonBreakdown) {
          if (b.items && Object.keys(b.items).length > 0) {
            for (const [label, nativeVal] of Object.entries(b.items)) {
              const isUsd = label.endsWith('(USD)');
              const cleanLabel = isUsd ? label.replace(/\s*\(USD\)$/, '') : label;
              const usdVal = isUsd ? nativeVal : (b.rate > 0 ? nativeVal / b.rate : 0);
              flat.push({
                month: b.month,
                concepto: cleanLabel,
                moneda: isUsd ? 'USD' : 'ARS',
                nativo: nativeVal,
                rate: b.rate,
                usd: usdVal,
              });
            }
          } else {
            // Fallback: solo total mensual.
            flat.push({ month: b.month, concepto: 'Total ARS', moneda: 'ARS', nativo: b.ars, rate: b.rate, usd: b.ars && b.rate ? b.ars / b.rate : 0 });
            if (b.usd_direct > 0) {
              flat.push({ month: b.month, concepto: 'USD directo', moneda: 'USD', nativo: b.usd_direct, rate: b.rate, usd: b.usd_direct });
            }
          }
        }
        return {
          title: `Gastos Comunes — ${periodStr}`,
          subtitle: `Bonos Comerciales, Cargas Sociales, Monotributo, Autónomos, Contador, G Suites, Telefonía, Viáticos, Comidas, Gastos Extraordinarios, BIG BOX, Cadetería, Mant. Cta Bancaria, Alquiler (ARS) + Gasto Liquidez y Costo Financiero (USD) de la pestaña EDR.`,
          rows: flat.filter(r => Math.abs(r.nativo) > 0),
          columns: [
            { key: 'month', label: 'Mes', render: (r) => monthLabel(r.month) },
            { key: 'concepto', label: 'Concepto' },
            { key: 'moneda', label: 'Mon.' },
            { key: 'nativo', label: 'Nativo', align: 'right', render: (r) => r.nativo.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'rate', label: 'TC', align: 'right', render: (r) => r.moneda === 'USD' ? '—' : r.rate.toLocaleString('es-AR', { maximumFractionDigits: 0 }) },
            { key: 'usd', label: 'USD', align: 'right', render: (r) => fmtExact(r.usd, 'USD') },
          ],
          totalUsd: data.commonUsd,
        };
      }
      default:
        return null;
    }
  }, [detailKey, data, isYear, selection]);

  return (
    <div className="px-6 py-6 space-y-6 max-w-6xl mx-auto" data-testid="edr-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.06em' }}>
            Estado de Resultados
          </h1>
          <p className="text-xs text-gray-500 font-mono mt-1">USD · {periodLabel}</p>
        </div>
        <Select value={selection} onValueChange={setSelection}>
          <SelectTrigger
            data-testid="edr-period-select"
            className="bg-gray-900 border border-gray-700 text-white text-lg font-semibold px-4 py-3 h-auto focus:outline-none focus:ring-2 focus:ring-yellow-500 min-w-[280px]"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[60vh]">
            <SelectItem value={YEAR} className="font-bold">Total {YEAR}</SelectItem>
            {monthsAll2026.map((m) => (
              <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <EdrUnmatchedRows rows={edr?.unmatched_rows} />

      {/* Two-column AR | INTL */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ARGENTINA */}
        <Card className="border-blue-500/20" style={{ backgroundColor: '#111' }} data-testid="edr-ar-block">
          <CardContent className="p-5 space-y-3">
            <h3 className="text-sm uppercase tracking-widest text-sky-300 font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}>
              Argentina
            </h3>
            <PnLRow label="Ventas AR"              value={data.ventaArUsd}   dim onDetail={() => setDetailKey('ventaAr')} />
            <PnLRow label="(−) Costos Directos AR" value={-data.costoArUsd}  dim onDetail={() => setDetailKey('costoAr')} />
            <PnLRow label="(−) Sueldos Arg"        value={-data.ar.sueldos}  dim onDetail={() => setDetailKey('sueldosAr')} />
            <PnLRow label="(−) Bonos Arg"          value={-data.ar.bonos}    dim sublabel="Bonos Comerciales" onDetail={() => setDetailKey('bonosAr')} />
            <PnLRow label="(−) Cargas Sociales AR" value={-data.ar.cargas}   dim sublabel="Cargas Sociales + Monotributo + Autónomos" onDetail={() => setDetailKey('cargasAr')} />
            <div className="border-t-2 border-sky-500/40 pt-3 mt-2">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-bold uppercase tracking-wider text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.06em' }}>
                  Resultado AR<Pct value={data.margenArPct} accent="#38BDF8" />
                </span>
                <span className="text-xl font-bold" style={{ color: data.resultadoAr >= 0 ? '#38BDF8' : '#E63946' }}>
                  {fmt(data.resultadoAr, 'USD')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* INTERNACIONAL */}
        <Card className="border-emerald-500/20" style={{ backgroundColor: '#111' }} data-testid="edr-intl-block">
          <CardContent className="p-5 space-y-3">
            <h3 className="text-sm uppercase tracking-widest text-emerald-300 font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}>
              Internacional
            </h3>
            <PnLRow label="Ventas INTL"              value={data.ventaIntlUsd}  dim onDetail={() => setDetailKey('ventaIntl')} />
            <PnLRow label="(−) Costos Directos INTL" value={-data.costoIntlUsd} dim onDetail={() => setDetailKey('costoIntl')} />
            <PnLRow label="(−) Sueldos Inter"        value={-data.intl.sueldos} dim onDetail={() => setDetailKey('sueldosIntl')} />
            <PnLRow label="(−) Bonos Inter"          value={-data.intl.bonos}   dim sublabel="Bonos Comerciales (sección INT, USD)" onDetail={() => setDetailKey('bonosIntl')} />
            <PnLRow label="(−) Cargas Sociales INT"  value={-data.intl.cargas}  dim sublabel="Cargas Sociales / Monotributo / Autónomos (sección INT)" onDetail={() => setDetailKey('cargasIntl')} />
            <PnLRow label="(−) Gastos Financieros INT" value={-data.intl.gastos_financieros} dim sublabel="Gasto Liquidez + Costo Financiero (USD)" onDetail={() => setDetailKey('finIntl')} />
            <PnLRow label="(−) Otros Gastos INT"      value={-data.intl.otros_gastos} dim sublabel="Dominio G Suites · Computadoras · Gastos Extraord. (sección INT, USD)" onDetail={() => setDetailKey('otrosGastosIntl')} />
            <div className="border-t-2 border-emerald-500/40 pt-3 mt-2">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-bold uppercase tracking-wider text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.06em' }}>
                  Resultado INTL<Pct value={data.margenIntlPct} accent="#34D399" />
                </span>
                <span className="text-xl font-bold" style={{ color: data.resultadoIntl >= 0 ? '#34D399' : '#E63946' }}>
                  {fmt(data.resultadoIntl, 'USD')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CONSOLIDADO: combinado → common → final */}
      <Card className="border-yellow-500/20" style={{ backgroundColor: '#111' }} data-testid="edr-consolidado">
        <CardContent className="p-6 space-y-5">
          <h3 className="text-xs uppercase tracking-widest text-yellow-300 font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.12em' }}>
            Consolidado
          </h3>

          <div className="border-t border-gray-800 pt-2">
            <PnLRow label="Resultado Combinado (AR + INTL)" value={data.resultadoCombinado} isSubtotal accent="#FFD166" />
          </div>

          <div className="border-t border-gray-800 pt-2">
            <PnLRow
              label="(−) Gastos Comunes"
              value={-data.commonUsd}
              sublabel="Bonos Com., Cargas, Monotrib., Autónomos, Contador, G Suites, Telefonía, Viáticos, Comidas, Extraord., BIG BOX, Cadetería, Mant. Cta, Alquiler + Gasto Liquidez y Costo Financ. (USD)"
              onDetail={() => setDetailKey('gastosComunes')}
            />
          </div>

          <div className="border-t-2 border-white/20 pt-5 mt-2">
            <div className="p-5 rounded-lg" style={{ backgroundColor: data.resultadoFinal >= 0 ? '#1B6B3A22' : '#E6394622', border: `2px solid ${data.resultadoFinal >= 0 ? '#1B6B3A' : '#E63946'}` }}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-300 font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.12em' }}>
                    Resultado Final — Talengers
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">= Resultado Combinado − Gastos Comunes</p>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold" style={{ color: data.resultadoFinal >= 0 ? '#1B6B3A' : '#E63946', fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em' }}>
                    {fmt(data.resultadoFinal, 'USD')}
                  </p>
                  <p className="text-sm font-semibold mt-1" style={{ color: data.margenFinalPct >= 0 ? '#1B6B3A' : '#E63946' }}>
                    Margen Final: {data.margenFinalPct.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {!data.hasAnyOpex && (
            <p className="text-xs text-gray-500 italic border-t border-gray-800 pt-3">
              ⚠️ No hay datos de la solapa <span className="font-mono text-gray-400">Gastos</span>. Cargá el Excel actualizado.
            </p>
          )}
        </CardContent>
      </Card>

      {detailConfig && (
        <DetailModal
          open={!!detailKey}
          onOpenChange={(v) => { if (!v) setDetailKey(null); }}
          title={detailConfig.title}
          subtitle={detailConfig.subtitle}
          rows={detailConfig.rows}
          columns={detailConfig.columns}
          totalUsd={detailConfig.totalUsd}
          note={detailConfig.note}
        />
      )}
    </div>
  );
};

export default EdrView;
